import { useCallback, useEffect, useMemo, useState } from 'react';
import { getAdminSession, isSuperAdmin } from '../adminSession';
import {
  fetchAdminAttendanceCalendar,
  fetchAdminAttendanceList,
  fetchAttendanceStaff,
  getAttendanceErrorMessage,
  postCreateAttendanceTimes,
  postRevertClockOut,
  postUpdateAttendanceTimes,
  type AttendanceRecord,
  type AttendanceStaffMember,
} from './adminAttendanceApi';
import './AdminAttendance.scss';

type AttendanceEditModal =
  | { mode: 'edit'; record: AttendanceRecord }
  | { mode: 'create'; workDate: string; userId: number }
  | null;

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];
const REPRESENTATIVE_ROLE = 'admin';

function isRepresentative(member: { role: string | null }) {
  return member.role === REPRESENTATIVE_ROLE;
}

function normalizeWorkDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = String(value).trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function normalizeRecord(record: AttendanceRecord): AttendanceRecord {
  return {
    ...record,
    userId: Number(record.userId),
    workDate: normalizeWorkDate(record.workDate) ?? record.workDate,
  };
}

type CalendarCell = {
  date: string | null;
  day: number | null;
  isCurrentMonth: boolean;
  isToday: boolean;
};

function todayParts() {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1, date: d.getDate() };
}

function toDateKey(year: number, month: number, day: number) {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function formatShortTime(value: string | null | undefined) {
  if (!value) return null;

  const normalized = String(value).trim().replace(/：/g, ':');
  const direct = normalized.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (direct) {
    return `${String(direct[1]).padStart(2, '0')}:${direct[2]}`;
  }

  const embedded = normalized.match(/(?:T|\s)(\d{1,2}):(\d{2})(?::\d{2})?/);
  if (embedded) {
    return `${String(embedded[1]).padStart(2, '0')}:${embedded[2]}`;
  }

  const parsed = new Date(normalized.includes('T') ? normalized : normalized.replace(' ', 'T'));
  if (Number.isNaN(parsed.getTime())) return null;

  return `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`;
}

function toTimeInputValue(value: string | null | undefined) {
  return formatShortTime(value) ?? '';
}

function normalizeTimeInput(value: string) {
  return formatShortTime(value) ?? value.trim().replace(/：/g, ':');
}

function buildCalendarCells(year: number, month: number): CalendarCell[] {
  const today = todayParts();
  const firstWeekday = new Date(year, month - 1, 1).getDay();
  const daysInMonth = new Date(year, month, 0).getDate();
  const prevMonthDays = new Date(year, month - 1, 0).getDate();
  const cells: CalendarCell[] = [];

  for (let i = firstWeekday - 1; i >= 0; i -= 1) {
    const day = prevMonthDays - i;
    const prevMonth = month === 1 ? 12 : month - 1;
    const prevYear = month === 1 ? year - 1 : year;
    cells.push({
      date: toDateKey(prevYear, prevMonth, day),
      day,
      isCurrentMonth: false,
      isToday: false,
    });
  }

  for (let day = 1; day <= daysInMonth; day += 1) {
    const date = toDateKey(year, month, day);
    cells.push({
      date,
      day,
      isCurrentMonth: true,
      isToday:
        today.year === year && today.month === month && today.date === day,
    });
  }

  while (cells.length % 7 !== 0) {
    const nextIndex = cells.length - (firstWeekday + daysInMonth) + 1;
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    cells.push({
      date: toDateKey(nextYear, nextMonth, nextIndex),
      day: nextIndex,
      isCurrentMonth: false,
      isToday: false,
    });
  }

  return cells;
}

function getAttendanceStatus(record: AttendanceRecord | undefined) {
  if (!record) return 'none';
  if (record.clockOut) return 'complete';
  return 'working';
}

function parseTimeToMinutes(value: string | null | undefined): number | null {
  const time = formatShortTime(value);
  if (!time) return null;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

function calcWorkMinutes(
  clockIn: string | null | undefined,
  clockOut: string | null | undefined,
): number | null {
  const inMin = parseTimeToMinutes(clockIn);
  const outMin = parseTimeToMinutes(clockOut);
  if (inMin === null || outMin === null) return null;
  const diff = outMin - inMin;
  return diff > 0 ? diff : null;
}

function formatWorkDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}분`;
  if (m === 0) return `${h}시간`;
  return `${h}시간 ${m}분`;
}

function includesLunch(
  clockIn: string | null | undefined,
  clockOut: string | null | undefined,
): boolean {
  const inMin = parseTimeToMinutes(clockIn);
  const outMin = parseTimeToMinutes(clockOut);
  if (inMin === null || outMin === null) return false;
  return inMin < 13 * 60 && outMin > 12 * 60;
}

export default function AdminAttendance() {
  const session = getAdminSession();
  const superAdmin = isSuperAdmin(session);
  const today = todayParts();

  const [viewYear, setViewYear] = useState(today.year);
  const [viewMonth, setViewMonth] = useState(today.month);
  const [staff, setStaff] = useState<AttendanceStaffMember[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);
  const [summaryRecords, setSummaryRecords] = useState<AttendanceRecord[]>([]);
  const [todayRecords, setTodayRecords] = useState<AttendanceRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<number | null>(null);
  const [editModal, setEditModal] = useState<AttendanceEditModal>(null);
  const [editClockIn, setEditClockIn] = useState('');
  const [editClockOut, setEditClockOut] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const loadStaff = useCallback(async () => {
    if (!session?.id) return;
    try {
      const list = await fetchAttendanceStaff(session.id);
      setStaff(list);
      setSelectedUserId((prev) => {
        if (!superAdmin) return session.id;
        if (prev && list.some((member) => member.id === prev)) return prev;
        return list[0]?.id ?? session.id;
      });
    } catch (err) {
      setError(getAttendanceErrorMessage(err, '직원 목록을 불러오지 못했습니다.'));
      setStaff([]);
    }
  }, [session?.id, superAdmin]);

  const loadCalendar = useCallback(async () => {
    if (!session?.id) {
      setRecords([]);
      setSummaryRecords([]);
      setTodayRecords([]);
      return;
    }

    const targetUserId = superAdmin ? selectedUserId ?? session.id : session.id;
    const todayKey = toDateKey(today.year, today.month, today.date);

    setLoading(true);
    setError(null);
    try {
      if (superAdmin) {
        const [calendarData, todayData, summaryData] = await Promise.all([
          fetchAdminAttendanceCalendar(session.id, viewYear, viewMonth, targetUserId),
          fetchAdminAttendanceList(session.id, todayKey),
          fetchAdminAttendanceCalendar(session.id, viewYear, viewMonth),
        ]);
        setRecords(calendarData.items.map(normalizeRecord));
        setSummaryRecords(summaryData.items.map(normalizeRecord));
        setTodayRecords(todayData.items.map(normalizeRecord));
      } else {
        const [calendarData, todayData] = await Promise.all([
          fetchAdminAttendanceCalendar(session.id, viewYear, viewMonth, targetUserId),
          fetchAdminAttendanceList(session.id, todayKey),
        ]);
        setRecords(calendarData.items.map(normalizeRecord));
        setSummaryRecords(calendarData.items.map(normalizeRecord));
        setTodayRecords(todayData.items.map(normalizeRecord));
      }
    } catch (err) {
      setError(getAttendanceErrorMessage(err, '출퇴근 현황을 불러오지 못했습니다.'));
      setRecords([]);
      setSummaryRecords([]);
      setTodayRecords([]);
    } finally {
      setLoading(false);
    }
  }, [
    session?.id,
    superAdmin,
    selectedUserId,
    viewYear,
    viewMonth,
    today.year,
    today.month,
    today.date,
  ]);

  useEffect(() => {
    void loadStaff();
  }, [loadStaff]);

  useEffect(() => {
    void loadCalendar();
  }, [loadCalendar]);

  const selectedRecords = useMemo(
    () =>
      selectedUserId
        ? records.filter((record) => Number(record.userId) === Number(selectedUserId))
        : [],
    [records, selectedUserId],
  );

  const recordsByDate = useMemo(() => {
    const map = new Map<string, AttendanceRecord>();
    selectedRecords.forEach((record) => {
      if (record.workDate) map.set(record.workDate, record);
    });
    return map;
  }, [selectedRecords]);

  const calendarSummary = useMemo(() => {
    const withClockOut = selectedRecords.filter((r) => r.clockIn && r.clockOut);
    const totalWorkMinutes = withClockOut.reduce<number>(
      (acc, r) => acc + (calcWorkMinutes(r.clockIn, r.clockOut) ?? 0),
      0,
    );
    const lunchCount = withClockOut.filter((r) =>
      includesLunch(r.clockIn, r.clockOut),
    ).length;
    const netWorkMinutes = Math.max(0, totalWorkMinutes - lunchCount * 60);
    return {
      workDayCount: selectedRecords.length,
      totalWorkMinutes,
      lunchCount,
      netWorkMinutes,
    };
  }, [selectedRecords]);

  const monthCountSource = superAdmin ? summaryRecords : records;

  const staffSummaries = useMemo(() => {
    const todayByUser = new Map<number, AttendanceRecord>();
    todayRecords.forEach((record) => {
      todayByUser.set(record.userId, record);
    });

    return staff.map((member) => ({
      ...member,
      monthCount: monthCountSource.filter(
        (record) => Number(record.userId) === Number(member.id),
      ).length,
      todayRecord: todayByUser.get(member.id),
    }));
  }, [staff, monthCountSource, todayRecords]);

  const calendarCells = useMemo(
    () => buildCalendarCells(viewYear, viewMonth),
    [viewYear, viewMonth],
  );

  const selectedStaff = staff.find((member) => member.id === selectedUserId);

  const monthLabel = `${viewYear}년 ${viewMonth}월`;

  const goPrevMonth = () => {
    if (viewMonth === 1) {
      setViewYear((y) => y - 1);
      setViewMonth(12);
      return;
    }
    setViewMonth((m) => m - 1);
  };

  const goNextMonth = () => {
    if (viewMonth === 12) {
      setViewYear((y) => y + 1);
      setViewMonth(1);
      return;
    }
    setViewMonth((m) => m + 1);
  };

  const goToday = () => {
    setViewYear(today.year);
    setViewMonth(today.month);
  };

  const openEditModal = (record: AttendanceRecord) => {
    if (!superAdmin) return;
    setEditModal({ mode: 'edit', record });
    setEditClockIn(toTimeInputValue(record.clockIn));
    setEditClockOut(toTimeInputValue(record.clockOut));
    setError(null);
  };

  const openCreateModal = (workDate: string) => {
    if (!superAdmin || !selectedUserId || isRepresentative({ role: selectedStaff?.role ?? null })) {
      return;
    }
    setEditModal({ mode: 'create', workDate, userId: selectedUserId });
    setEditClockIn('');
    setEditClockOut('');
    setError(null);
  };

  const closeEditModal = () => {
    if (savingEdit) return;
    setEditModal(null);
    setEditClockIn('');
    setEditClockOut('');
    setError(null);
  };

  const handleSaveEdit = async () => {
    if (!superAdmin || !session?.id || !editModal) return;
    if (!editClockIn.trim()) {
      setError('출근 시각을 입력해 주세요.');
      return;
    }

    const clockInValue = normalizeTimeInput(editClockIn);
    const clockOutValue = editClockOut.trim() ? normalizeTimeInput(editClockOut) : '';

    if (!/^\d{2}:\d{2}$/.test(clockInValue)) {
      setError('출근 시각은 HH:mm 형식이어야 합니다.');
      return;
    }
    if (clockOutValue && !/^\d{2}:\d{2}$/.test(clockOutValue)) {
      setError('퇴근 시각은 HH:mm 형식이어야 합니다.');
      return;
    }

    setSavingEdit(true);
    setError(null);
    try {
      if (editModal.mode === 'edit') {
        await postUpdateAttendanceTimes(
          session.id,
          editModal.record.id,
          clockInValue,
          clockOutValue,
        );
      } else {
        await postCreateAttendanceTimes(
          session.id,
          editModal.userId,
          editModal.workDate,
          clockInValue,
          clockOutValue,
        );
      }
      setEditModal(null);
      setEditClockIn('');
      setEditClockOut('');
      await loadCalendar();
    } catch (err) {
      const fallback =
        editModal.mode === 'edit'
          ? '출퇴근 시간 수정에 실패했습니다.'
          : '출퇴근 시간 입력에 실패했습니다.';
      setError(getAttendanceErrorMessage(err, fallback));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleRevertClockOut = async (record: AttendanceRecord) => {
    if (!superAdmin || !session?.id || !record.clockOut) return;
    const name = record.name ?? selectedStaff?.name ?? '직원';
    const ok = window.confirm(
      `「${name}」 직원의 ${record.workDate} 퇴근 기록을 되돌리시겠습니까?`,
    );
    if (!ok) return;

    setRevertingId(record.id);
    setError(null);
    try {
      await postRevertClockOut(session.id, record.id);
      setEditModal(null);
      setEditClockIn('');
      setEditClockOut('');
      await loadCalendar();
    } catch (err) {
      setError(getAttendanceErrorMessage(err, '퇴근 되돌리기에 실패했습니다.'));
    } finally {
      setRevertingId(null);
    }
  };

  return (
    <div className={`admin-attendance${superAdmin ? '' : ' admin-attendance--self-only'}`}>
      <header className="admin-attendance__header">
        <div>
          <h2 className="admin-attendance__title">출퇴근 현황</h2>
          <p className="admin-attendance__subtitle">
            {superAdmin
              ? '전체 직원의 출퇴근을 조회할 수 있습니다. 대표자 본인 기록은 대표자만 확인할 수 있습니다.'
              : '본인의 월간 출퇴근 기록만 확인할 수 있습니다.'}
            {superAdmin ? ' 출퇴근 시간 입력·수정은 직원 기록에만 가능합니다.' : ''}
          </p>
        </div>
        <button
          type="button"
          className="admin-attendance__refresh-btn"
          onClick={() => {
            void loadStaff();
            void loadCalendar();
          }}
          disabled={loading}
        >
          {loading ? '불러오는 중…' : '새로고침'}
        </button>
      </header>

      {error ? (
        <p className="admin-attendance__error" role="alert">
          {error}
        </p>
      ) : null}

      <section className="admin-attendance__staff-section" aria-label="직원별 출퇴근 요약">
        <h3 className="admin-attendance__section-title">
          {superAdmin ? '직원별 현황' : '내 출퇴근 요약'}
        </h3>
        <div className={`admin-attendance__staff-grid${superAdmin ? '' : ' admin-attendance__staff-grid--single'}`}>
          {staffSummaries.length === 0 ? (
            <p className="admin-attendance__staff-empty">조회 가능한 출퇴근 정보가 없습니다.</p>
          ) : (
            staffSummaries.map((member) => {
              const isSelected = member.id === selectedUserId;
              const todayStatus = member.todayRecord
                ? member.todayRecord.clockOut
                  ? `퇴근 ${formatShortTime(member.todayRecord.clockOut)}`
                  : `출근 ${formatShortTime(member.todayRecord.clockIn)}`
                  : '오늘 미출근';
              const isSelfRepresentative = isRepresentative(member);

              if (!superAdmin) {
                return (
                  <article key={member.id} className="admin-attendance__staff-card admin-attendance__staff-card--static is-selected">
                    <div className="admin-attendance__staff-card-top">
                      <strong className="admin-attendance__staff-name">{member.name}</strong>
                      <span className="admin-attendance__staff-meta">
                        {[member.department, member.position].filter(Boolean).join(' · ') || '—'}
                      </span>
                    </div>
                    <div className="admin-attendance__staff-stats">
                      <span className="admin-attendance__staff-stat">
                        이번 달 출근 <b>{member.monthCount}</b>일
                      </span>
                      <span className="admin-attendance__staff-today">{todayStatus}</span>
                    </div>
                  </article>
                );
              }

              return (
                <button
                  key={member.id}
                  type="button"
                  className={`admin-attendance__staff-card${isSelected ? ' is-selected' : ''}${isSelfRepresentative ? ' is-representative' : ''}`}
                  onClick={() => setSelectedUserId(member.id)}
                >
                  <div className="admin-attendance__staff-card-top">
                    <strong className="admin-attendance__staff-name">
                      {member.name}
                      {isSelfRepresentative ? ' (대표)' : ''}
                    </strong>
                    <span className="admin-attendance__staff-meta">
                      {[member.department, member.position].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </div>
                  <div className="admin-attendance__staff-stats">
                    <span className="admin-attendance__staff-stat">
                      이번 달 출근 <b>{member.monthCount}</b>일
                    </span>
                    <span className="admin-attendance__staff-today">{todayStatus}</span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </section>

      <section className="admin-attendance__calendar-section" aria-label="월간 출퇴근 달력">
        <div className="admin-attendance__calendar-toolbar">
          <div className="admin-attendance__calendar-nav">
            <button type="button" className="admin-attendance__nav-btn" onClick={goPrevMonth}>
              ‹
            </button>
            <h3 className="admin-attendance__calendar-title">
              {monthLabel}
              {selectedStaff ? ` · ${selectedStaff.name}` : ''}
            </h3>
            <button type="button" className="admin-attendance__nav-btn" onClick={goNextMonth}>
              ›
            </button>
          </div>

          {calendarSummary.workDayCount > 0 ? (
            <div className="admin-attendance__calendar-summary">
              <span className="admin-attendance__calendar-summary-stat">
                출근 <b>{calendarSummary.workDayCount}</b>일
              </span>
              <span className="admin-attendance__calendar-summary-divider" />
              <span className="admin-attendance__calendar-summary-formula">
                <b>{formatWorkDuration(calendarSummary.totalWorkMinutes)}</b>
                <span className="admin-attendance__calendar-summary-op">−</span>
                <span className="admin-attendance__calendar-summary-lunch">
                  점심 <b>{calendarSummary.lunchCount}</b>일
                </span>
                <span className="admin-attendance__calendar-summary-op">=</span>
                <b className="admin-attendance__calendar-summary-net">
                  {formatWorkDuration(calendarSummary.netWorkMinutes)}
                </b>
              </span>
            </div>
          ) : null}

          <button type="button" className="admin-attendance__today-btn" onClick={goToday}>
            오늘
          </button>
        </div>

        <div className="admin-attendance__legend">
          <span className="admin-attendance__legend-item">
            <i className="admin-attendance__legend-dot is-complete" />
            퇴근 완료
          </span>
          <span className="admin-attendance__legend-item">
            <i className="admin-attendance__legend-dot is-working" />
            출근만
          </span>
          <span className="admin-attendance__legend-item">
            <i className="admin-attendance__legend-dot is-none" />
            기록 없음
          </span>
        </div>

        <div className="admin-attendance__calendar">
          <div className="admin-attendance__weekdays">
            {WEEKDAY_LABELS.map((label, index) => (
              <div
                key={label}
                className={`admin-attendance__weekday${index === 0 ? ' is-sun' : ''}${index === 6 ? ' is-sat' : ''}`}
              >
                {label}
              </div>
            ))}
          </div>

          <div className="admin-attendance__days">
            {calendarCells.map((cell) => {
              const record = cell.date ? recordsByDate.get(cell.date) : undefined;
              const status = getAttendanceStatus(record);
              const clockIn = formatShortTime(record?.clockIn);
              const clockOut = formatShortTime(record?.clockOut);
              const workMinutes = calcWorkMinutes(record?.clockIn, record?.clockOut);
              const showLunch = includesLunch(record?.clockIn, record?.clockOut);

              return (
                <div
                  key={`${cell.date}-${cell.day}`}
                  className={[
                    'admin-attendance__day',
                    !cell.isCurrentMonth ? 'is-outside' : '',
                    cell.isToday ? 'is-today' : '',
                    status !== 'none' ? `is-${status}` : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <div className="admin-attendance__day-head">
                    <span className="admin-attendance__day-num">{cell.day}</span>
                  </div>

                  {record ? (
                    <div className="admin-attendance__day-body">
                      <div className="admin-attendance__time-row">
                        <span className="admin-attendance__time-label">출</span>
                        <span className="admin-attendance__time-value">{clockIn ?? '—'}</span>
                      </div>
                      <div className="admin-attendance__time-row">
                        <span className="admin-attendance__time-label">퇴</span>
                        <span className="admin-attendance__time-value">{clockOut ?? '—'}</span>
                      </div>
                      {workMinutes !== null ? (
                        <div className="admin-attendance__work-duration">
                          {formatWorkDuration(workMinutes)}
                          {showLunch ? (
                            <span className="admin-attendance__lunch-badge">점심</span>
                          ) : null}
                        </div>
                      ) : null}
                      {superAdmin && selectedStaff && !isRepresentative(selectedStaff) ? (
                        <button
                          type="button"
                          className="admin-attendance__edit-btn"
                          disabled={loading || savingEdit}
                          onClick={() => openEditModal(record)}
                        >
                          수정
                        </button>
                      ) : null}
                    </div>
                  ) : superAdmin &&
                    cell.isCurrentMonth &&
                    cell.date &&
                    selectedUserId &&
                    selectedStaff &&
                    !isRepresentative(selectedStaff) ? (
                    <div className="admin-attendance__day-empty">
                      <button
                        type="button"
                        className="admin-attendance__edit-btn"
                        disabled={loading || savingEdit}
                        onClick={() => openCreateModal(cell.date!)}
                      >
                        입력
                      </button>
                    </div>
                  ) : (
                    <div className="admin-attendance__day-empty">—</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {loading ? <p className="admin-attendance__loading">달력 데이터를 불러오는 중…</p> : null}
      </section>

      {superAdmin && editModal ? (
        <div className="admin-attendance__modal-backdrop" role="presentation" onClick={closeEditModal}>
          <div
            className="admin-attendance__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-attendance-edit-title"
            onClick={(event) => event.stopPropagation()}
          >
            <h3 id="admin-attendance-edit-title" className="admin-attendance__modal-title">
              {editModal.mode === 'edit' ? '출퇴근 시간 수정' : '출퇴근 시간 입력'}
            </h3>
            <p className="admin-attendance__modal-desc">
              {selectedStaff?.name ?? (editModal.mode === 'edit' ? editModal.record.name : '직원')} ·{' '}
              {editModal.mode === 'edit' ? editModal.record.workDate : editModal.workDate}
            </p>

            <div className="admin-attendance__modal-fields">
              <label className="admin-attendance__modal-label">
                출근 시각
                <input
                  type="time"
                  step="60"
                  className="admin-attendance__modal-input"
                  value={editClockIn}
                  onChange={(event) => setEditClockIn(event.target.value)}
                  disabled={savingEdit}
                />
              </label>
              <label className="admin-attendance__modal-label">
                퇴근 시각
                <input
                  type="time"
                  step="60"
                  className="admin-attendance__modal-input"
                  value={editClockOut}
                  onChange={(event) => setEditClockOut(event.target.value)}
                  disabled={savingEdit}
                />
              </label>
            </div>

            <div className="admin-attendance__modal-actions">
              {editModal.mode === 'edit' && editModal.record.clockOut ? (
                <button
                  type="button"
                  className="admin-attendance__revert-btn"
                  disabled={savingEdit || revertingId === editModal.record.id}
                  onClick={() => void handleRevertClockOut(editModal.record)}
                >
                  {revertingId === editModal.record.id ? '처리 중' : '퇴근 되돌리기'}
                </button>
              ) : null}
              <div className="admin-attendance__modal-actions-right">
                <button
                  type="button"
                  className="admin-attendance__modal-cancel"
                  onClick={closeEditModal}
                  disabled={savingEdit}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="admin-attendance__modal-save"
                  onClick={() => void handleSaveEdit()}
                  disabled={savingEdit}
                >
                  {savingEdit ? '저장 중…' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
